import React, { SyntheticEvent, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { addSchedule } from '../../../api';
import { ControlledTextField } from '../../../components/FormControlsV2/ControlledTextField';
import { FormProvider } from '../../../components/FormControlsV2/FormProvider';
import { ERRORS_TEXTS } from '../../../const/validation';
import { scheduleApi } from '../../../effector/schedule';
import { MarketSchedule } from '../../../types/marketSchedule';
import { Option } from '../../../types/option';

type Props = {
  availableMarkets: Option[];
  onClose: () => void;
};

type FormFields = {
  markets: Option[];
  description: string;
};

const validationSchema = Yup.object({
  markets: Yup.array()
    .of(
      Yup.object({
        id: Yup.string().required(),
        name: Yup.string().required(),
      }),
    )
    .min(1, 'Не выбран код торговой площадки'),
  description: Yup.string().required(ERRORS_TEXTS.required),
});

export const ScheduleAddDialog: React.FC<Props> = ({ availableMarkets, onClose }) => {
  const [send, setSend] = useState(false);
  const [error, setError] = useState<string>();

  const defaultValues = useMemo<FormFields>(
    () => ({
      markets: [],
      description: '',
    }),
    [],
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FormFields>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as never,
  });

  const handleClose = () => {
    if (!send) {
      setError(undefined);
      onClose();
    }
  };

  const onSubmit: SubmitHandler<FormFields> = async (values) => {
    setSend(true);
    setError(undefined);

    const data: MarketSchedule = {
      id: 0,
      classCodes: values.markets.map((market) => market.id),
      name: values.description,
      addForbiddenSecurities: null,
      removeForbiddenSecurities: null,
      tradeTimes: [],
      signalTimes: [],
      rebalanceTimes: [],
      weekends: [],
    };

    try {
      const response = await addSchedule(data);

      if (response?.success) {
        scheduleApi.set(response.result as MarketSchedule);
        onClose();
        return;
      }

      setError(response?.errorMessage ?? 'Ошибка на сервере');
      setSend(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSend(false);
    }
  };

  return (
    <Dialog open onClose={send ? undefined : handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Добавление расписания
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={send}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormProvider control={control} errors={errors}>
          <DialogContent dividers>
            <Stack spacing={2.5}>
              <Typography>Добавление расписания для новой торговой площадки</Typography>
              <Controller
                name="markets"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    multiple
                    options={availableMarkets}
                    value={field.value}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(_: SyntheticEvent, newValue) => {
                      field.onChange(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Коды площадки"
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />
              <ControlledTextField name="description" label="Название площадки" margin="none" />
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={send}>
              Отмена
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!isDirty || send}
              startIcon={send ? <CircularProgress size={18} color="inherit" /> : null}
            >
              Сохранить
            </Button>
          </DialogActions>
        </FormProvider>
      </form>
    </Dialog>
  );
};
